import { useState } from 'react'

import { Working } from '~/components/working'
import { reached, removed, useWork } from '~/components/use-work'
import { AmountStep } from '~/components/amount-step'
import { openingAmount } from '~/lib/opening-amount'
import { ConfirmDialog } from '~/components/confirm-dialog'
import { FoodMark } from '~/components/food-mark'
import { FoodSearch, MyFoodsList } from '~/components/food-search'
import { DeleteIcon } from '~/components/icons'
import { NutrientFields } from '~/components/nutrient-fields'
import { PageDialog } from '~/components/page-dialog'
import { useToast } from '~/components/toast-context'
import { UnitInput } from '~/components/unit-input'
import { useApi } from '~/data/data-context'
import { useAskingForForm } from '~/data/food-store'
import {
  EMPTY_NUTRIENTS,
  amountSaid,
  foodKey,
  macroLine,
  nutrientsFor,
  recipeFood,
  recipeGrams,
  recipeNameFrom,
  scaled,
  summed,
  type Amount,
  type Food,
  type Ingredient,
} from '~/data/nutrition'
import { useFocusOnMount } from '~/lib/focus-on-mount'
import {
  EMPTY_NUTRIENT_TEXT,
  nutrientText,
  nutrientsTyped,
  type NutrientText,
} from '~/lib/nutrient-text'

/** The one amount a quick add counts as, the same as an entry made without a food. */
const QUICK_AMOUNT: Amount = { value: 1, unit: 'portion', portion: 'serving' }

/**
 * A recipe's form, per the spec's Recipes: a name, the servings the whole makes, and its
 * ingredients — each a food searched for the way an entry's is, and an amount of it, or a
 * quick add: a name and the nutrients typed outright, counting as one amount. It says what
 * the recipe comes to per serving and per hundred grams as the ingredients change. Opened
 * from the diary it arrives holding chosen entries as its ingredients, a quick add among
 * them as a quick add.
 */
export function RecipeFormDialog({
  recipe = null,
  seed = [],
  onClose,
  onSaved,
  onDeleted,
}: {
  /** The recipe being edited, or null for a new one. */
  recipe?: Food | null
  /** Ingredients to open holding, where the recipe is being made of a day's entries. */
  seed?: readonly Ingredient[]
  onClose: () => void
  /** Told once it is saved; what it hands back is waited on with the key still working. */
  onSaved: (saved: Food) => void | Promise<void>
  onDeleted?: ((id: string) => void) | undefined
}) {
  const api = useApi()
  const toast = useToast()
  /*
   * A recipe made out of a day's entries opens named after them — see `recipeNameFrom`. The
   * name takes the cursor and is selected with it, so it is typed over by anybody who wants
   * their own and kept by anybody who does not.
   */
  const suggested = recipeNameFrom(seed.map((each) => each.name))
  const [name, setName] = useState(recipe?.name ?? suggested)
  const [servings, setServings] = useState(
    recipe?.recipe === null || recipe === null ? '1' : String(recipe.recipe.servings),
  )
  const [ingredients, setIngredients] = useState<Ingredient[]>(() => [
    ...(recipe?.recipe?.ingredients ?? []),
    ...seed,
  ])
  const [adding, setAdding] = useState<'search' | 'mine' | null>(null)
  /*
   * The picker the recipe opens for itself asks as a form — `/specs/features/food-order.spec.md`:
   * its plate is what the recipe already holds, so what goes with the eggs already in it stands
   * first, and nothing about the time of day moves the list.
   */
  const asking = useAskingForForm(
    ingredients.flatMap((each) => (each.food === null ? [] : [foodKey(each.food)])),
  )
  const [measuring, setMeasuring] = useState<{
    food: Food
    amount: Amount
    at: number | null
  } | null>(null)
  /** A quick add being typed — new, or one already among the ingredients opened again. */
  const [quick, setQuick] = useState<{
    name: string
    text: NutrientText
    at: number | null
  } | null>(null)
  const saving = useWork()
  const [leaving, setLeaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const nameField = useFocusOnMount<HTMLInputElement>()

  const dirty =
    // The name the form suggested is not something typed, so it is not something to lose.
    name !== (recipe?.name ?? suggested) ||
    servings !== (recipe?.recipe ? String(recipe.recipe.servings) : '1') ||
    JSON.stringify(ingredients) !==
      JSON.stringify([...(recipe?.recipe?.ingredients ?? []), ...seed])
  const servingsNumber = Number(servings)
  const complete =
    name.trim() !== '' &&
    ingredients.length > 0 &&
    Number.isFinite(servingsNumber) &&
    servingsNumber > 0

  /*
   * A weight typed by hand stands, whatever the ingredients would have added up to — the
   * nutrition spec, What a recipe weighs. This form does not take one, so it carries the one
   * the recipe already has rather than dropping it: a rename here must not undo a weight
   * written elsewhere.
   */
  const totalGrams = recipe?.recipe?.totalGrams ?? null
  const total = summed(ingredients.map((ingredient) => ingredient.nutrients))
  const grams = recipeGrams({ servings: 1, ingredients, totalGrams })
  const perServing = servingsNumber > 0 ? scaled(total, 1 / servingsNumber) : null
  const perHundred = grams > 0 ? scaled(total, 100 / grams) : null

  const askToClose = () => {
    if (dirty) {
      setLeaving(true)
    } else {
      onClose()
    }
  }

  const keepIngredient = () => {
    if (measuring === null) {
      return
    }
    const nutrients = nutrientsFor(measuring.food, measuring.amount)
    if (nutrients === null || measuring.amount.value <= 0) {
      return
    }
    const ingredient: Ingredient = {
      name: measuring.food.name,
      food: measuring.food,
      amount: measuring.amount,
      nutrients,
    }
    setIngredients((held) =>
      measuring.at === null
        ? [...held, ingredient]
        : held.map((each, at) => (at === measuring.at ? ingredient : each)),
    )
    setMeasuring(null)
    setAdding(null)
  }

  const quickTyped = quick === null ? null : nutrientsTyped(quick.text)
  const keepQuick = () => {
    if (quick === null || quickTyped === null || quick.name.trim() === '') {
      return
    }
    const ingredient: Ingredient = {
      name: quick.name.trim(),
      food: null,
      amount: QUICK_AMOUNT,
      nutrients: { ...EMPTY_NUTRIENTS, ...quickTyped },
    }
    setIngredients((held) =>
      quick.at === null
        ? [...held, ingredient]
        : held.map((each, at) => (at === quick.at ? ingredient : each)),
    )
    setQuick(null)
    setAdding(null)
  }

  const save = () =>
    saving.run(async () => {
      if (!complete) {
        return
      }
      try {
        const id = recipe?.id ?? crypto.randomUUID()
        const base = {
          id,
          name: name.trim(),
          emoji: null,
          brand: null,
          barcode: null,
        }
        const draft = recipeFood(base, { servings: servingsNumber, ingredients, totalGrams })
        const saved = await api.putFood(id, {
          name: base.name,
          reference: draft.reference,
          nutrients: draft.nutrients,
          portions: draft.portions,
          recipe: { servings: servingsNumber, ingredients, totalGrams },
        })
        toast(recipe === null ? 'Recipe saved.' : 'Recipe updated.')
        await onSaved(saved)
      } catch {
        toast('That did not reach us. Try again in a moment.')
      }
    })

  const remove = async () => {
    if (recipe?.id == null || onDeleted === undefined) {
      return
    }
    const id = recipe.id
    await reached(() => removed(() => api.deleteFood(id)))
    toast(`${recipe.name} deleted.`)
    onDeleted(recipe.id)
  }

  const box =
    'w-full rounded-2xl bg-surface p-3 text-lg outline-none ring-1 ring-white/10 focus:ring-2 focus:ring-accent'

  return (
    <PageDialog
      title={recipe === null ? 'New recipe' : 'Edit recipe'}
      onClose={askToClose}
      className="max-w-xl"
    >
      {measuring !== null ? (
        <div className="space-y-4">
          <h2 className="text-lg font-medium">{measuring.food.name}</h2>
          <AmountStep
            food={measuring.food}
            amount={measuring.amount}
            onAmount={(next) => {
              setMeasuring({ ...measuring, amount: next })
            }}
          />
          <div className="flex justify-end gap-3">
            <button
              type="button"
              className="rounded-full px-4 py-2 text-sm"
              onClick={() => {
                setMeasuring(null)
              }}
            >
              Back
            </button>
            <button
              type="button"
              disabled={measuring.amount.value <= 0}
              className="rounded-full bg-accent px-5 py-2 text-sm font-medium text-accent-ink disabled:opacity-50"
              onClick={keepIngredient}
            >
              {measuring.at === null ? 'Add ingredient' : 'Keep'}
            </button>
          </div>
        </div>
      ) : quick !== null ? (
        <div className="space-y-4">
          <h2 className="text-lg font-medium">
            {quick.at === null ? 'Quick entry' : quick.name || 'Quick entry'}
          </h2>
          <label className="block">
            <span className="mb-1 block text-sm text-ink-muted">Name</span>
            <input
              aria-label="Ingredient name"
              value={quick.name}
              onChange={(event) => {
                setQuick({ ...quick, name: event.target.value })
              }}
              className={box}
            />
          </label>
          <NutrientFields
            text={quick.text}
            onText={(next) => {
              setQuick({ ...quick, text: next })
            }}
          />
          <div className="flex justify-end gap-3">
            <button
              type="button"
              className="rounded-full px-4 py-2 text-sm"
              onClick={() => {
                setQuick(null)
              }}
            >
              Back
            </button>
            <button
              type="button"
              disabled={quick.name.trim() === '' || quickTyped === null}
              className="rounded-full bg-accent px-5 py-2 text-sm font-medium text-accent-ink disabled:opacity-50"
              onClick={keepQuick}
            >
              {quick.at === null ? 'Add ingredient' : 'Keep'}
            </button>
          </div>
        </div>
      ) : adding !== null ? (
        <div className="space-y-4">
          <div className="flex gap-2">
            <Tab
              label="Search"
              on={adding === 'search'}
              onPick={() => {
                setAdding('search')
              }}
            />
            <Tab
              label="Mine"
              on={adding === 'mine'}
              onPick={() => {
                setAdding('mine')
              }}
            />
            <Tab
              label="Quick entry"
              on={false}
              onPick={() => {
                setQuick({ name: '', text: EMPTY_NUTRIENT_TEXT, at: null })
              }}
            />
            <span className="flex-1" />
            <button
              type="button"
              className="rounded-full px-4 py-2 text-sm"
              onClick={() => {
                setAdding(null)
              }}
            >
              Back
            </button>
          </div>
          {adding === 'search' ? (
            <FoodSearch
              picked={new Set()}
              asking={asking}
              onPick={(food) => {
                setMeasuring({ food, amount: openingAmount(food, null), at: null })
              }}
            />
          ) : (
            <MyFoodsList
              picked={new Set()}
              asking={asking}
              onPick={(food) => {
                setMeasuring({ food, amount: openingAmount(food, null), at: null })
              }}
            />
          )}
        </div>
      ) : (
        <form
          className="space-y-5"
          onSubmit={(event) => {
            event.preventDefault()
            void save()
          }}
        >
          {/* The recipe's deck stands beside its name, as a food's face stands beside its own,
              and follows the name and the ingredients as they change. */}
          <div className="grid grid-cols-[auto_1fr_8rem] items-end gap-3">
            <FoodMark
              food={{ name, recipe: { servings: 1, ingredients } }}
              size="size-12 text-2xl"
            />
            <label className="block">
              <span className="mb-1 block text-sm text-ink-muted">Name</span>
              <input
                aria-label="Name"
                ref={nameField}
                value={name}
                onChange={(event) => {
                  setName(event.target.value)
                }}
                className={box}
              />
            </label>
            <UnitInput value={servings} unit="servings" label="Makes" onValue={setServings} />
          </div>

          <div className="space-y-2">
            <h3 className="text-sm text-ink-muted">Ingredients</h3>
            {ingredients.length === 0 ? (
              <p className="px-3 py-4 text-center text-sm text-ink-muted">No ingredients yet.</p>
            ) : (
              <ul className="space-y-0.5">
                {ingredients.map((ingredient, index) => (
                  <li
                    key={`${foodKey(ingredient.food ?? ({ id: null, libraryCode: null, name: ingredient.name } as Food))}-${String(index)}`}
                    className="flex items-center gap-2"
                  >
                    <button
                      type="button"
                      className="flex min-w-0 flex-1 items-center gap-3 rounded-2xl px-3 py-2.5 text-left transition-colors hover:bg-hover"
                      onClick={() => {
                        if (ingredient.food === null) {
                          setQuick({
                            name: ingredient.name,
                            text: nutrientText(ingredient.nutrients),
                            at: index,
                          })
                        } else {
                          setMeasuring({
                            food: ingredient.food,
                            amount: ingredient.amount,
                            at: index,
                          })
                        }
                      }}
                    >
                      {ingredient.food === null ? (
                        <FoodMark name={ingredient.name} />
                      ) : (
                        <FoodMark food={ingredient.food} />
                      )}
                      <span className="min-w-0 flex-1">
                        <span className="block truncate">
                          {ingredient.name}
                          {/* A quick add counts as one amount of itself; saying so says nothing. */}
                          {ingredient.food !== null && (
                            <span className="text-ink-muted">
                              {' '}
                              · {amountSaid(ingredient.amount)}
                            </span>
                          )}
                        </span>
                        <span className="block truncate text-sm text-ink-muted">
                          {macroLine(ingredient.nutrients)}
                        </span>
                      </span>
                    </button>
                    <button
                      type="button"
                      aria-label={`Remove ${ingredient.name}`}
                      className="icon-btn shrink-0"
                      onClick={() => {
                        setIngredients((held) => held.filter((_, at) => at !== index))
                      }}
                    >
                      <DeleteIcon aria-hidden className="size-6" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <button
              type="button"
              className="rounded-full px-4 py-2 text-sm text-accent transition-colors hover:bg-hover"
              onClick={() => {
                setAdding('search')
              }}
            >
              Add an ingredient
            </button>
          </div>

          <dl className="grid grid-cols-2 gap-2 text-sm">
            <div className="rounded-xl bg-surface-raised px-3 py-2">
              <dt className="text-xs text-ink-muted">Per serving</dt>
              <dd>{perServing === null ? '—' : macroLine(perServing)}</dd>
            </div>
            <div className="rounded-xl bg-surface-raised px-3 py-2">
              <dt className="text-xs text-ink-muted">Per 100 g</dt>
              <dd>{perHundred === null ? '—' : macroLine(perHundred)}</dd>
            </div>
          </dl>

          <div className="flex items-center gap-3 pt-1">
            {recipe !== null && onDeleted !== undefined && (
              <button
                type="button"
                className="flex items-center gap-1.5 rounded-full px-3 py-2 text-sm text-danger transition-colors hover:bg-danger/10"
                onClick={() => {
                  setDeleting(true)
                }}
              >
                <DeleteIcon aria-hidden className="size-5" />
                Delete
              </button>
            )}
            <span className="flex-1" />
            <button
              type="submit"
              aria-busy={saving.working || undefined}
              disabled={!complete}
              className="relative rounded-full bg-accent px-5 py-2 text-sm font-medium text-accent-ink disabled:opacity-50"
            >
              <Working shown={saving.shown}>Save</Working>
            </button>
          </div>
        </form>
      )}

      {leaving && (
        <ConfirmDialog
          title="Discard this recipe?"
          body="What you have entered is not saved."
          confirm="Discard"
          onConfirm={onClose}
          onDismiss={() => {
            setLeaving(false)
          }}
        />
      )}
      {deleting && recipe !== null && (
        <ConfirmDialog
          title={`Delete ${recipe.name}?`}
          body="Entries already logged with it keep what they say. The recipe itself goes."
          confirm="Delete"
          onConfirm={remove}
          onDismiss={() => {
            setDeleting(false)
          }}
        />
      )}
    </PageDialog>
  )
}

/** One of a row of tabs across the top of a sheet. */
export function Tab({ label, on, onPick }: { label: string; on: boolean; onPick: () => void }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={on}
      onClick={onPick}
      className={`rounded-full px-4 py-2 text-sm transition-colors ${
        on ? 'bg-hover font-medium text-ink' : 'text-ink-muted hover:bg-hover-faint hover:text-ink'
      }`}
    >
      {label}
    </button>
  )
}
